// src/components/Fields/DatePickerSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useFieldEdit } from '../../../../contexts/FieldEditContext';
import debounce from 'lodash/debounce';

// Date format options – these correspond directly to the metadata
// that DateFieldComponent + date-fns understand (after token mapping).
const dateFormats = [
  { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
  { label: 'MM/DD', value: 'MM/DD' },
  { label: 'MM/DD/YY', value: 'MM/DD/YY' },
  { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
  { label: 'DD/MM', value: 'DD/MM' },
  { label: 'YYYY/MM/DD', value: 'YYYY/MM/DD' },
  { label: 'Month Day (e.g. November 21)', value: 'MMMM D' },
  { label: 'Abbreviated Month Day (e.g. Nov 21)', value: 'MMM D' },
  { label: 'Abbrev Month / DD / YYYY (e.g. Dec/04/2025)', value: 'MMM/DD/YYYY' },
];

// Date separator options – values here are actual characters used
// by DateFieldComponent to normalize the format string.
const dateSeparators = [
  { label: 'Dash (-)', value: '-' },
  { label: 'Slash (/)', value: '/' },
  { label: 'Space ( )', value: ' ' },
  { label: 'Underscore (_)', value: '_' },
  { label: 'None (join parts)', value: '' },
];

// Capitalization options – used by DateFieldComponent to post-process
// the formatted string returned from date-fns.
const capitalizationOptions = [
  { label: 'No restrictions', value: 'none' },
  { label: 'ALL CAPS', value: 'all-caps' },
  { label: 'no caps', value: 'no-caps' },
  { label: 'First letter only', value: 'first-letter' },
];

const DatePickerSettings = () => {
  const { selectedFieldForEdit, updateField } = useFieldEdit();
  const [localValues, setLocalValues] = useState({});

  // Load metadata into local state on selection
  useEffect(() => {
    if (selectedFieldForEdit && selectedFieldForEdit.metadata) {
      const meta = selectedFieldForEdit.metadata;
      setLocalValues({
        ...meta,
        // Ensure we always have a transformations object with sensible defaults.
        transformations: {
          encode: false,
          capitalize: 'none',
          spacing: '/',
          ...(meta.transformations || {}),
        },
      });
    }
  }, [selectedFieldForEdit]);

  const debouncedUpdateField = useCallback(
    debounce((id, newMetadata) => {
      updateField(id, newMetadata);
    }, 300),
    [updateField]
  );

  // Generic handler for top-level date-only fields (currently just dateFormat)
  const handleChange = useCallback(
    (prop, event) => {
      const newValue = event.target.value || '';
      setLocalValues((prevValues) => {
        const updatedValues = {
          ...prevValues,
          [prop]: newValue,
        };

        // 🔎 DIAG: see what we're writing for date-specific metadata
        console.log('[DatePickerSettings] handleChange → prop:', prop, 'value:', newValue);
        console.log('[DatePickerSettings] Updated localValues (date-only):', updatedValues);

        // Debounced write into FieldMetadataContext
        debouncedUpdateField(selectedFieldForEdit.id, updatedValues);

        return updatedValues;
      });
    },
    [selectedFieldForEdit?.id, debouncedUpdateField]
  );

  // Handler for nested transformations (spacing, capitalize) – date-specific flavor
  const handleTransformationChange = useCallback(
    (prop, event) => {
      const newValue = event.target.value;

      setLocalValues((prev) => {
        const prevTransforms = prev.transformations || {
          encode: false,
          capitalize: 'none',
          spacing: '/',
        };

        const next = {
          ...prev,
          transformations: {
            ...prevTransforms,
            [prop]: newValue,
          },
        };

        // 🔎 DIAG: see date-specific transformations we are updating
        console.log('[DatePickerSettings] handleTransformationChange → prop:', prop, 'value:', newValue);
        console.log('[DatePickerSettings] Next transformations:', next.transformations);

        // Debounced write using the freshly computed object
        debouncedUpdateField(selectedFieldForEdit.id, next);
        return next;
      });
    },
    [selectedFieldForEdit?.id, debouncedUpdateField]
  );

  if (!selectedFieldForEdit || !selectedFieldForEdit.metadata) {
    return <Typography>Select a field to edit</Typography>;
  }

  return (
    <Box sx={{ maxWidth: '90%', mx: 'auto' }}>
      {/* Title */}
      <Typography
        variant="body2"
        sx={{ ml: 1, mb: 1.5, mt: 1, fontWeight: 'bold', color: '#003459' }}
      >
        Date Field Settings (Advanced)
      </Typography>

      {/* Date Type – fixed to single-date, shown for clarity */}
      <FormControl fullWidth margin="normal" sx={{ my: 2 }}>
        <InputLabel>Date Type</InputLabel>
        <Select
          label="Date Type"
          value="single"
          disabled
        >
          <MenuItem value="single">Single Date</MenuItem>
        </Select>
        <Typography variant="caption" sx={{ mt: 1 }}>
          This field captures a single date. To build a range, use two fields such as “Start Date”
          and “End Date”.
        </Typography>
      </FormControl>

      {/* Date Format Selection */}
      <FormControl fullWidth margin="normal" sx={{ my: 2 }}>
        <InputLabel>Date Format</InputLabel>
        <Select
          label="Date Format"
          value={localValues.dateFormat || 'MM/DD/YYYY'}
          onChange={(e) => handleChange('dateFormat', e)}
          displayEmpty
        >
          {dateFormats.map((format) => (
            <MenuItem key={format.value} value={format.value}>
              {format.label}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" sx={{ mt: 1 }}>
          Choose how the date will be displayed in this field. This controls both the input format
          and the stored string value.
        </Typography>
      </FormControl>

      {/* Date Separator Selection */}
      <FormControl fullWidth margin="normal" sx={{ my: 2 }}>
        <InputLabel>Date Separator</InputLabel>
        <Select
          label="Date Separator"
          value={
            localValues.transformations?.spacing !== undefined
              ? localValues.transformations.spacing
              : '/'
          }
          onChange={(e) => handleTransformationChange('spacing', e)}
          displayEmpty
        >
          {dateSeparators.map((separator) => (
            <MenuItem key={separator.value} value={separator.value}>
              {separator.label}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" sx={{ mt: 1 }}>
          Select the character that separates date parts (month, day, year). This is applied to the
          format above (for example: `MM-DD-YYYY` vs `MM/DD/YYYY`).
        </Typography>
      </FormControl>

      {/* Capitalization Settings */}
      <FormControl fullWidth margin="normal" sx={{ my: 2 }}>
        <InputLabel>Capitalization Settings</InputLabel>
        <Select
          label="Capitalization Settings"
          value={localValues.transformations?.capitalize || 'none'}
          onChange={(e) => handleTransformationChange('capitalize', e)}
          displayEmpty
        >
          {capitalizationOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" sx={{ mt: 1 }}>
          Choose how month names are capitalized (for example “november 21” vs “NOVEMBER 21”). This
          is applied after the date is formatted.
        </Typography>
      </FormControl>
    </Box>
  );
};

export default DatePickerSettings;
